/* eslint-disable react/no-unused-prop-types */
import * as React from "react";
import PausedClusterQueuesNotice from "app/components/shared/PausedClusterQueuesNotice";
import Header from "../Header";
import BuildContentNavigation from "./BuildContentNavigation";
import BuildShowStore, { Build, FaviconPaths } from "app/stores/BuildShowStore";
import {
  BuildContextProvider,
  useBuild,
} from "app/components/Playground/BuildContext";
import JobsPage from "./JobsPage";
import SummaryPage from "./SummaryPage";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useMatches,
  useNavigate,
} from "react-router-dom";
import BuildPageStore, { parseHash } from "app/stores/BuildPageStore";
import Footer from "app/components/layout/Footer";
import { StepDrawer } from "./StepDrawer";
import { DrawerOutlet } from "./DrawerOutlet";
import { ErrorView } from "app/components/shared/ErrorView";

const Canvas = React.lazy(
  () =>
    import(
      /* webpackChunkName: "canvas" */ "app/components/Playground/BuildCanvas"
    ),
);

export enum BuildView {
  Summary = "SUMMARY",
  JobList = "JOB_LIST",
  Waterfall = "WATERFALL",
  Canvas = "CANVAS",
}

/**
 * Build page layout.
 */
export function BuildShow() {
  const matches = useMatches();
  const { build, store } = useBuild();
  if (!build || !store) {
    throw new Error("Missing build context");
  }

  const view = [...matches].pop()?.id as BuildView;
  return (
    <>
      <PausedClusterQueuesNotice
        pausedQueues={build.dispatchPausedClusterQueues}
      />

      <Header
        build={build}
        showRebuild={true}
        store={store}
        currentView={view}
        showJobs={view === BuildView.JobList || view === BuildView.Summary}
      />
      <BuildContentNavigation
        buildPath={build.path}
        currentView={view}
        waterfallAvailable={store.waterfallAvailable}
      />
      <Outlet />
    </>
  );
}

/**
 * Render a list of jobs that expand based on the URL hash.
 */
const Jobs = () => {
  const { store } = useBuild();
  const { hash } = useLocation();

  if (!store) {
    throw new Error("Missing build context");
  }

  React.useLayoutEffect(() => {
    const { job } = parseHash(hash) || {};
    if (job) {
      BuildPageStore.expandJob({ id: job });
    }
  }, [hash]);

  return (
    <>
      <JobsPage store={store} />
      <Footer />
    </>
  );
};

/**
 * A container for the non-react waterfall view.
 */
const Waterfall = () => {
  const { build } = useBuild();

  if (!build) {
    throw new Error("Missing build context");
  }

  return <turbo-frame id="waterfall" src={`${build.path}/waterfall_tab`} />;
};

const router = createBrowserRouter([
  {
    path: "/:org/:pipeline/builds/:number/",
    element: <BuildShow />,
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        id: BuildView.JobList,
        element: <Jobs />,
        errorElement: <RouteError />,
      },
      {
        path: "summary",
        id: BuildView.Summary,
        element: Features.BuildSidebar ? <SummaryPage /> : <Navigate to=".." />,
        errorElement: <RouteError />,
      },
      {
        path: "canvas",
        id: BuildView.Canvas,
        errorElement: <RouteError />,
        element: (
          <div
            className="flex-1 relative rounded-md mb-4 border border-gray-300 data-[fullscreen=true]:fixed data-[fullscreen=true]:w-screen data-[fullscreen=true]:h-screen data-[fullscreen=true]:border-none data-[fullscreen=true]:rounded-none data-[fullscreen=true]:left-0 data-[fullscreen=true]:top-0 bg-white data-[fullscreen=true]:m-0 overflow-hidden"
            id="canvas"
          >
            <div className="absolute w-full h-full">
              <React.Suspense fallback={null}>
                <Canvas />
              </React.Suspense>
            </div>
            <DrawerOutlet />
          </div>
        ),
        children: [
          {
            path: ":stepId",
            element: Features.BuildSidebar ? (
              <StepDrawer />
            ) : (
              <Navigate to=".." />
            ),
          },
        ],
      },
      {
        path: "waterfall",
        id: BuildView.Waterfall,
        element: <Waterfall />,
        errorElement: <RouteError />,
      },
    ],
  },
]);

function RouteError() {
  const navigate = useNavigate();

  // eslint-disable-next-line react/jsx-no-bind
  return <ErrorView clearError={() => navigate(0)} />;
}

interface PageProps {
  build: Build;
  faviconPaths: FaviconPaths;
  waterfallAvailable: boolean;
}

export default (props: PageProps) => {
  const store = new BuildShowStore(props);

  return (
    <BuildContextProvider store={store}>
      <RouterProvider router={router} />
    </BuildContextProvider>
  );
};
